import React, { useState } from 'react';
import {
  CreateIndicatorRequest,
  IndicatorResponse,
  FormulaDto,
  ConclusionRuleDto,
  ConditionType,
  ConditionConfig,
  ScoreRangeCondition,
  ScoreEqualsCondition,
  TextEqualsCondition,
  ExpressionCondition
} from '../services/indicatorService';
import { Question } from '../types';
import { TrashIcon } from './Icons';
import { HelpButton } from './HelpDialog';
import { HELP_INDICATOR } from './HelpContent';

// Conclusion Rule Editor Component
export const ConclusionRuleEditor: React.FC<{
  rule: ConclusionRuleDto;
  questions: Question[];
  onChange: (rule: ConclusionRuleDto) => void;
  onDelete: () => void;
}> = ({ rule, questions, onChange, onDelete }) => {
  const updateConditionType = (type: ConditionType) => {
    let newConfig: ConditionConfig;

    switch (type) {
      case 'SCORE_RANGE':
        newConfig = {
          type: 'SCORE_RANGE',
          min: 0,
          max: null,
          minInclusive: true,
          maxInclusive: false,
        };
        break;
      case 'SCORE_EQUALS':
        newConfig = {
          type: 'SCORE_EQUALS',
          value: 0,
        };
        break;
      case 'TEXT_EQUALS':
        newConfig = {
          type: 'TEXT_EQUALS',
          questionId: questions[0]?.id || 0,
          expectedText: '',
        };
        break;
      case 'EXPRESSION':
        newConfig = {
          type: 'EXPRESSION',
          expression: '',
        };
        break;
    }

    onChange({ ...rule, conditionType: type, conditionConfig: newConfig });
  };

  const updateScoreRange = (updates: Partial<ScoreRangeCondition>) => {
    onChange({
      ...rule,
      conditionConfig: { ...rule.conditionConfig, ...updates } as ScoreRangeCondition,
    });
  };

  const updateScoreEquals = (value: number) => {
    onChange({
      ...rule,
      conditionConfig: { type: 'SCORE_EQUALS', value } as ScoreEqualsCondition,
    });
  };

  const updateTextEquals = (updates: Partial<Omit<TextEqualsCondition, 'type'>>) => {
    onChange({
      ...rule,
      conditionConfig: { ...rule.conditionConfig, ...updates } as TextEqualsCondition,
    });
  };

  const updateExpression = (expression: string) => {
    onChange({
      ...rule,
      conditionConfig: { type: 'EXPRESSION', expression } as ExpressionCondition,
    });
  };

  return (
    <div className="shadow-soft rounded-2xl p-4">
      <div className="flex justify-between items-start mb-3">
        <h4 className="text-sm font-medium font-label text-on-surface">规则 {rule.orderNum + 1}</h4>
        <button
          type="button"
          onClick={onDelete}
          className="text-red-600 hover:text-red-700"
        >
          <TrashIcon size={18} />
        </button>
      </div>

      {/* Condition Type */}
      <div className="mb-3">
        <label className="block text-sm font-medium font-label text-on-surface mb-2">条件类型</label>
        <select
          value={rule.conditionType}
          onChange={(e) => updateConditionType(e.target.value as ConditionType)}
          className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
        >
          <option value="SCORE_RANGE">分数区间 - 简单范围判断</option>
          <option value="SCORE_EQUALS">分数等于 - 精确匹配特定分值</option>
          <option value="TEXT_EQUALS">文本等于 - 匹配某题的文本答案</option>
          <option value="EXPRESSION">表达式 - 复杂逻辑和多题计算</option>
        </select>
        <p className="mt-1 text-xs text-on-surface-variant font-label">
          {rule.conditionType === 'SCORE_RANGE' && '使用指标得分 (SCORE) 进行区间判断'}
          {rule.conditionType === 'SCORE_EQUALS' && '使用指标得分 (SCORE) 进行精确匹配'}
          {rule.conditionType === 'TEXT_EQUALS' && '匹配某个问题的文本答案'}
          {rule.conditionType === 'EXPRESSION' && '可使用 SCORE、Q[题号]、TOTAL_SCORE、AVG_SCORE 等变量'}
        </p>
      </div>

      {/* Condition Config */}
      {rule.conditionType === 'SCORE_RANGE' && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm text-on-surface-variant mb-1 font-label">最小值</label>
            <div className="flex items-center space-x-2">
              <select
                value={(rule.conditionConfig as ScoreRangeCondition).minInclusive ? 'inclusive' : 'exclusive'}
                onChange={(e) => updateScoreRange({ minInclusive: e.target.value === 'inclusive' })}
                className="px-2 py-1 bg-surface-container-high rounded-xl"
              >
                <option value="inclusive">≥</option>
                <option value="exclusive">&gt;</option>
              </select>
              <input
                type="number"
                value={(rule.conditionConfig as ScoreRangeCondition).min ?? ''}
                onChange={(e) => updateScoreRange({ min: e.target.value ? parseFloat(e.target.value) : null })}
                className="flex-1 px-3 py-1 bg-surface-container-high rounded-xl"
                placeholder="0"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-on-surface-variant mb-1 font-label">最大值</label>
            <div className="flex items-center space-x-2">
              <select
                value={(rule.conditionConfig as ScoreRangeCondition).maxInclusive ? 'inclusive' : 'exclusive'}
                onChange={(e) => updateScoreRange({ maxInclusive: e.target.value === 'inclusive' })}
                className="px-2 py-1 bg-surface-container-high rounded-xl"
              >
                <option value="inclusive">≤</option>
                <option value="exclusive">&lt;</option>
              </select>
              <input
                type="number"
                value={(rule.conditionConfig as ScoreRangeCondition).max ?? ''}
                onChange={(e) => updateScoreRange({ max: e.target.value ? parseFloat(e.target.value) : null })}
                className="flex-1 px-3 py-1 bg-surface-container-high rounded-xl"
                placeholder="无上限"
              />
            </div>
          </div>
        </div>
      )}

      {rule.conditionType === 'SCORE_EQUALS' && (
        <div className="mb-3">
          <label className="block text-sm text-on-surface-variant mb-1 font-label">分数值</label>
          <input
            type="number"
            step="0.1"
            value={(rule.conditionConfig as ScoreEqualsCondition).value}
            onChange={(e) => updateScoreEquals(parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-surface-container-high rounded-xl"
            placeholder="例如：10"
          />
        </div>
      )}

      {rule.conditionType === 'TEXT_EQUALS' && (
        <div className="space-y-3 mb-3">
          <div>
            <label className="block text-sm text-on-surface-variant mb-1 font-label">选择问题</label>
            <select
              value={(rule.conditionConfig as TextEqualsCondition).questionId}
              onChange={(e) => updateTextEquals({ questionId: parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-surface-container-high rounded-xl"
            >
              {questions.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-on-surface-variant mb-1 font-label">期望文本</label>
            <input
              type="text"
              value={(rule.conditionConfig as TextEqualsCondition).expectedText}
              onChange={(e) => updateTextEquals({ expectedText: e.target.value })}
              className="w-full px-3 py-2 bg-surface-container-high rounded-xl"
              placeholder="例如：满意"
            />
          </div>
        </div>
      )}

      {rule.conditionType === 'EXPRESSION' && (
        <div className="mb-3">
          <label className="block text-sm text-on-surface-variant mb-1 font-label">表达式 *</label>
          <textarea
            value={(rule.conditionConfig as ExpressionCondition).expression}
            onChange={(e) => updateExpression(e.target.value)}
            className="w-full px-3 py-2 bg-surface-container-high rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
            rows={3}
            placeholder="例如：(Q1 + Q2) > 50"
          />
          <div className="mt-2 p-3 bg-blue-50 rounded-2xl text-xs space-y-1">
            <div className="font-medium text-blue-900 font-label">可用变量：</div>
            <div className="text-blue-800 font-body">
              * <code className="bg-surface-container-lowest px-1 py-0.5 rounded">Q[题号]</code> - 问题得分（如 Q1, Q2, Q3）
              <br />
              * <code className="bg-surface-container-lowest px-1 py-0.5 rounded">SCORE</code> - 指标得分（由上方公式计算）
              <br />
              * <code className="bg-surface-container-lowest px-1 py-0.5 rounded">TOTAL_SCORE</code> - 所有问题总分
              <br />
              * <code className="bg-surface-container-lowest px-1 py-0.5 rounded">AVG_SCORE</code> - 所有问题平均分
            </div>
            <div className="font-medium text-blue-900 mt-2 font-label">运算符：</div>
            <div className="text-blue-800 font-body">
              数学：<code className="bg-surface-container-lowest px-1 py-0.5 rounded">+ - * /</code>
              {' '}比较：<code className="bg-surface-container-lowest px-1 py-0.5 rounded">&gt; &gt;= &lt; &lt;= == !=</code>
              {' '}逻辑：<code className="bg-surface-container-lowest px-1 py-0.5 rounded">&& || !</code>
            </div>
            <div className="font-medium text-blue-900 mt-2 font-label">示例：</div>
            <div className="text-blue-800 font-mono font-body">
              (Q1 + Q2) &gt; 50 <span className="text-on-surface-variant">// 两题之和大于50</span>
              <br />
              (Q1 + Q2 + Q3) / 3 &gt;= 75 <span className="text-on-surface-variant">// 三题平均分≥75</span>
              <br />
              SCORE &gt;= 80 && Q1 &gt; 60 <span className="text-on-surface-variant">// 总分≥80且Q1及格</span>
            </div>
          </div>
        </div>
      )}

      {/* Conclusion */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm text-on-surface-variant mb-1 font-label">结论标题 *</label>
          <input
            type="text"
            value={rule.conclusionTitle}
            onChange={(e) => onChange({ ...rule, conclusionTitle: e.target.value })}
            className="w-full px-3 py-2 bg-surface-container-high rounded-xl"
            placeholder="例如：优秀"
          />
        </div>
        <div>
          <label className="block text-sm text-on-surface-variant mb-1 font-label">结论描述</label>
          <textarea
            value={rule.conclusionDescription || ''}
            onChange={(e) => onChange({ ...rule, conclusionDescription: e.target.value })}
            className="w-full px-3 py-2 bg-surface-container-high rounded-xl"
            rows={2}
            placeholder="详细描述该结论的含义"
          />
        </div>
        <div>
          <label className="block text-sm text-on-surface-variant mb-1 font-label">建议措施</label>
          <textarea
            value={rule.recommendations || ''}
            onChange={(e) => onChange({ ...rule, recommendations: e.target.value })}
            className="w-full px-3 py-2 bg-surface-container-high rounded-xl"
            rows={2}
            placeholder="针对该结论的建议"
          />
        </div>
        <div>
          <label className="block text-sm text-on-surface-variant mb-2 font-label">颜色</label>
          {[
            [
              { color: '#fecaca', label: '浅红' }, { color: '#fed7aa', label: '浅橙' },
              { color: '#fde68a', label: '浅黄' }, { color: '#bbf7d0', label: '浅绿' },
              { color: '#a7f3d0', label: '浅翠' }, { color: '#bae6fd', label: '浅蓝' },
              { color: '#c7d2fe', label: '浅靛' }, { color: '#ddd6fe', label: '浅紫' },
              { color: '#fbcfe8', label: '浅粉' }, { color: '#e5e7eb', label: '浅灰' },
            ],
            [
              { color: '#ef4444', label: '红色' }, { color: '#f97316', label: '橙色' },
              { color: '#f59e0b', label: '黄色' }, { color: '#84cc16', label: '黄绿' },
              { color: '#10b981', label: '绿色' }, { color: '#3b82f6', label: '蓝色' },
              { color: '#6366f1', label: '靛色' }, { color: '#8b5cf6', label: '紫色' },
              { color: '#ec4899', label: '粉色' }, { color: '#6b7280', label: '灰色' },
            ],
            [
              { color: '#991b1b', label: '深红' }, { color: '#9a3412', label: '深橙' },
              { color: '#92400e', label: '深黄' }, { color: '#3f6212', label: '深黄绿' },
              { color: '#065f46', label: '深绿' }, { color: '#1e40af', label: '深蓝' },
              { color: '#3730a3', label: '深靛' }, { color: '#5b21b6', label: '深紫' },
              { color: '#9d174d', label: '深粉' }, { color: '#374151', label: '深灰' },
            ],
          ].map((row, rowIdx) => (
            <div key={rowIdx} className="flex gap-1.5 mb-1.5">
              {row.map(({ color, label }) => (
                <button
                  key={color}
                  type="button"
                  title={label}
                  onClick={() => onChange({ ...rule, level: color })}
                  className={`w-7 h-7 rounded-xl border-2 transition-all ${
                    rule.level === color ? 'border-on-surface ring-2 ring-offset-1 ring-gray-400' : 'border-gray-200 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={rule.crisisAlert || false}
              onChange={(e) => onChange({ ...rule, crisisAlert: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm font-medium text-red-700 font-label">危机预警</span>
            <span className="text-xs text-on-surface-variant font-label">匹配此规则的用户将被标记为危机预警</span>
          </label>
        </div>
      </div>
    </div>
  );
};

// Indicator Form Modal Component
export const IndicatorFormModal: React.FC<{
  indicator: IndicatorResponse | null;
  questions: Question[];
  onClose: () => void;
  onSave: (request: CreateIndicatorRequest) => void;
}> = ({ indicator, questions, onClose, onSave }) => {
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    orderNum: number;
    formulaType: 'SUM' | 'AVERAGE' | 'WEIGHTED_SUM';
    selectedQuestions: Array<{ questionId: number; weight: number; matrixRows?: number[] }>;
    conclusionRules: ConclusionRuleDto[];
    enabled: boolean;
  }>(() => {
    if (indicator) {
      const formula = JSON.parse(indicator.formula);
      return {
        name: indicator.name,
        description: indicator.description || '',
        orderNum: indicator.orderNum,
        formulaType: formula.type,
        selectedQuestions: formula.questions,
        conclusionRules: indicator.conclusionRules.map(r => {
          const conditionValue = JSON.parse(r.conditionValue);
          return {
            conditionType: r.conditionType as ConditionType,
            conditionConfig: conditionValue,
            conclusionTitle: r.conclusionTitle,
            conclusionDescription: r.conclusionDescription,
            recommendations: r.recommendations,
            level: r.level as any,
            orderNum: r.orderNum,
            crisisAlert: r.crisisAlert || false,
          };
        }),
        enabled: indicator.enabled,
      };
    }
    return {
      name: '',
      description: '',
      orderNum: 0,
      formulaType: 'SUM',
      selectedQuestions: [],
      conclusionRules: [
        {
          conditionType: 'SCORE_RANGE',
          conditionConfig: {
            type: 'SCORE_RANGE',
            min: 0,
            max: null,
            minInclusive: true,
            maxInclusive: false,
          } as ScoreRangeCondition,
          conclusionTitle: '',
          conclusionDescription: '',
          recommendations: '',
          level: '#10b981',
          orderNum: 0,
        }
      ],
      enabled: true,
    };
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('请输入指标名称');
      return;
    }

    if (formData.selectedQuestions.length === 0) {
      alert('请至少选择一个问题');
      return;
    }

    if (formData.conclusionRules.some(r => !r.conclusionTitle.trim())) {
      alert('请填写所有结论规则的标题');
      return;
    }

    const formula: FormulaDto = {
      type: formData.formulaType,
      questions: formData.selectedQuestions.map(sq => ({
        questionId: sq.questionId,
        weight: sq.weight,
        // 仅当选了矩阵部分行时带上 matrixRows（全选/非矩阵不带 = 整题）
        ...(sq.matrixRows && sq.matrixRows.length > 0 ? { matrixRows: sq.matrixRows } : {}),
      })),
    };

    const request: CreateIndicatorRequest = {
      name: formData.name,
      description: formData.description || undefined,
      orderNum: formData.orderNum,
      formula,
      conclusionRules: formData.conclusionRules,
      enabled: formData.enabled,
    };

    onSave(request);
  };

  const addQuestion = (questionId: number) => {
    if (formData.selectedQuestions.find(q => q.questionId === questionId)) {
      return;
    }
    setFormData({
      ...formData,
      selectedQuestions: [...formData.selectedQuestions, { questionId, weight: 1.0 }],
    });
  };

  const removeQuestion = (questionId: number) => {
    setFormData({
      ...formData,
      selectedQuestions: formData.selectedQuestions.filter(q => q.questionId !== questionId),
    });
  };

  const updateWeight = (questionId: number, weight: number) => {
    setFormData({
      ...formData,
      selectedQuestions: formData.selectedQuestions.map(q =>
        q.questionId === questionId ? { ...q, weight } : q
      ),
    });
  };

  /** 解析矩阵题的行标签数组（来自 settings.rows）；非矩阵或无行返回 []。 */
  const getMatrixRowLabels = (question: Question): string[] => {
    if (question.type !== 'MATRIX' || !question.settings) return [];
    try {
      const s = JSON.parse(question.settings);
      return Array.isArray(s?.rows) ? s.rows as string[] : [];
    } catch {
      return [];
    }
  };

  /**
   * 切换某矩阵题的"参与计算行"。rowIndex 为 1-based。
   * 规则：全选 = matrixRows 设为 undefined（=整题）；部分选 = 存选中行的 1-based 序号(升序)。
   */
  const toggleMatrixRow = (questionId: number, totalRows: number, rowIndex: number) => {
    setFormData({
      ...formData,
      selectedQuestions: formData.selectedQuestions.map(q => {
        if (q.questionId !== questionId) return q;
        // 当前选中集合：undefined 视为全选
        const current = q.matrixRows && q.matrixRows.length > 0
          ? new Set(q.matrixRows)
          : new Set(Array.from({ length: totalRows }, (_, i) => i + 1));
        if (current.has(rowIndex)) current.delete(rowIndex); else current.add(rowIndex);
        const selected = Array.from(current).sort((a, b) => a - b);
        // 全选 → 不带 matrixRows（整题）；否则带选中行
        const matrixRows = selected.length === totalRows ? undefined : selected;
        return { ...q, matrixRows };
      }),
    });
  };

  /** 某矩阵题的某行是否参与计算（matrixRows 为空/undefined 视为全选）。 */
  const isMatrixRowSelected = (q: { matrixRows?: number[] }, rowIndex: number): boolean => {
    if (!q.matrixRows || q.matrixRows.length === 0) return true;
    return q.matrixRows.includes(rowIndex);
  };

  const addRule = () => {
    setFormData({
      ...formData,
      conclusionRules: [
        ...formData.conclusionRules,
        {
          conditionType: 'SCORE_RANGE',
          conditionConfig: {
            type: 'SCORE_RANGE',
            min: 0,
            max: null,
            minInclusive: true,
            maxInclusive: false,
          } as ScoreRangeCondition,
          conclusionTitle: '',
          conclusionDescription: '',
          recommendations: '',
          level: '#10b981',
          orderNum: formData.conclusionRules.length,
        },
      ],
    });
  };

  const removeRule = (index: number) => {
    setFormData({
      ...formData,
      conclusionRules: formData.conclusionRules.filter((_, i) => i !== index),
    });
  };

  const updateRule = (index: number, rule: ConclusionRuleDto) => {
    setFormData({
      ...formData,
      conclusionRules: formData.conclusionRules.map((r, i) => (i === index ? rule : r)),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface-container-lowest rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-soft">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <h2 className="text-xl font-semibold font-headline">
              {indicator ? '编辑指标' : '添加指标'}
            </h2>
            <HelpButton title="指标配置说明" content={HELP_INDICATOR} />
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-6">
            {/* Basic Info */}
            <div>
              <label className="block text-sm font-medium font-label text-on-surface mb-2">
                指标名称 *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:ring-2 focus:ring-primary-200 focus:outline-none"
                placeholder="例如：心理健康指数"
              />
            </div>

            <div>
              <label className="block text-sm font-medium font-label text-on-surface mb-2">
                指标描述
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:ring-2 focus:ring-primary-200 focus:outline-none"
                rows={3}
                placeholder="描述该指标的含义和用途"
              />
            </div>

            {/* Formula Section */}
            <div>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-medium font-headline text-on-surface">计算公式</h3>
                  <p className="text-sm text-on-surface-variant mt-1 font-body">
                    计算指标得分，用于统计分析和显示。结论规则中可使用 SCORE 变量引用此得分。
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium font-label text-on-surface mb-2">
                  公式类型
                </label>
                <select
                  value={formData.formulaType}
                  onChange={(e) => setFormData({ ...formData, formulaType: e.target.value as any })}
                  className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:ring-2 focus:ring-primary-200 focus:outline-none"
                >
                  <option value="SUM">求和 (Q1 + Q2 + ...)</option>
                  <option value="AVERAGE">平均值 ((Q1 + Q2 + ...) / n)</option>
                  <option value="WEIGHTED_SUM">加权求和 (Q1*w1 + Q2*w2 + ...)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium font-label text-on-surface mb-2">
                  选择问题 *
                </label>
                <div className="shadow-soft rounded-2xl p-4 max-h-72 overflow-y-auto">
                  {questions.map((question) => {
                    const selected = formData.selectedQuestions.find(q => q.questionId === question.id);
                    const rowLabels = getMatrixRowLabels(question);
                    const isMatrixWithRows = !!selected && rowLabels.length > 0;
                    return (
                    <div key={question.id} className="py-2 border-b border-outline-variant last:border-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                addQuestion(question.id);
                              } else {
                                removeQuestion(question.id);
                              }
                            }}
                            className="mr-3"
                          />
                          <span className="text-sm text-on-surface font-body">{question.title}</span>
                        </div>
                        {formData.formulaType === 'WEIGHTED_SUM' && selected && (
                          <input
                            type="number"
                            step="0.1"
                            value={selected.weight || 1}
                            onChange={(e) => updateWeight(question.id, parseFloat(e.target.value) || 1)}
                            className="w-20 px-2 py-1 bg-surface-container-high rounded-xl ml-3"
                            placeholder="权重"
                          />
                        )}
                      </div>
                      {/* 矩阵题：选中后可勾选"参与计算的行"。默认全选=整题。 */}
                      {isMatrixWithRows && (
                        <div className="ml-7 mt-2 pl-3 border-l-2 border-primary-100">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-on-surface-variant font-body">
                              参与计算的行（默认全选 = 整题；取消勾选可只算部分行）
                            </span>
                            <span className="text-xs text-primary font-body">
                              {selected!.matrixRows && selected!.matrixRows.length > 0
                                ? `已选 ${selected!.matrixRows.length}/${rowLabels.length} 行`
                                : `全部 ${rowLabels.length} 行`}
                            </span>
                          </div>
                          <div className="space-y-0.5">
                            {rowLabels.map((label, idx) => {
                              const rowNo = idx + 1; // 1-based
                              return (
                                <label key={rowNo} className="flex items-center text-xs text-on-surface font-body cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isMatrixRowSelected(selected!, rowNo)}
                                    onChange={() => toggleMatrixRow(question.id, rowLabels.length, rowNo)}
                                    className="mr-2"
                                  />
                                  <span className="text-on-surface-variant mr-1">{rowNo}.</span>
                                  <span className="truncate">{label.replace(/^\d+\.?\s*/, '')}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Conclusion Rules Section */}
            <div>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-medium font-headline text-on-surface">结论规则</h3>
                  <p className="text-sm text-on-surface-variant mt-1 font-body">
                    根据条件判定结论。规则按顺序匹配，返回第一个符合条件的结论。
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addRule}
                  className="px-3 py-1 gradient-primary text-on-primary shadow-teal text-sm rounded-xl hover:opacity-90 flex-shrink-0"
                >
                  添加规则
                </button>
              </div>

              <div className="space-y-4">
                {formData.conclusionRules.map((rule, index) => (
                  <ConclusionRuleEditor
                    key={index}
                    rule={rule}
                    questions={questions}
                    onChange={(updatedRule) => updateRule(index, updatedRule)}
                    onDelete={() => removeRule(index)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-surface-container-high rounded-xl hover:bg-surface-container-low"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 gradient-primary text-on-primary shadow-teal rounded-xl hover:opacity-90"
            >
              保存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
